import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { decodeId } from '../../utils/idCodec';
import ProductQrCard from './ProductQrCard';
import ImagePicker from '../../components/ImagePicker';
import { NameCell } from '../../hooks/useNameMaps';
import { persistImage } from '../../utils/persistImage';
import SectionsEditor, { type PanelSection } from '../../components/SectionsEditor';
import NumberField from '../../components/NumberField';
import {
  Alert, Button, Card, ImageGallery, SearchableSelect, StatusBadge, Tabs, useToast,
  type SearchableSelectOption,
} from '@kss/ui';
import {
  ChevronLeft, ChevronRight, Save, CheckCircle2, Plus, Trash2,
} from 'lucide-react';
import type {
  Product, ProductCreate, ProductVariant, ProductAttribute,
} from '@kss/api';
import {
  productService, categoryService, storeService,
  attributeService, attributeValueService,
  brandService, unitService,
} from '@kss/api';
import { useModulePerms } from '../../hooks/useModulePerms';

type TabStatus = 'default' | 'danger' | 'success' | 'warning';

interface VariantAttrPair { attributeId: string; attributeValueId: string }

type FormState = {
  name: string;
  sku: string;
  barcode: string;
  hsnCode: string;
  description: string;
  brand: string;
  unit: string;
  costPrice: number;
  sellingPrice: number;
  mrpPrice: number;
  taxRate: number;
  weight: number;
  dimensions: string;
  images: string[];
  /**
   * Optional admin-uploaded custom QR image URL. When set, the panel's
   * ProductQrCard renders this image instead of the auto-generated QR.
   * Persisted to `Product.customQrImageUrl` (nvarchar(2048) NULL).
   */
  customQrImageUrl: string;
  isActive: boolean;
  hasVariants: boolean;
  categoryId: string;
  storeId: string;
  variants: (ProductVariant & { __attrPairs?: VariantAttrPair[] })[];
  productAttributes: ProductAttribute[];
  /**
   * Dynamic accordion sections rendered on the storefront product
   * page. Serialised into `Product.sectionsJson` on save (see
   * BACKEND_AGENT_PROMPTS Item G — column pending).
   */
  sections: PanelSection[];
  /* ---- SEO + industry-standard identifiers ---- */
  slug: string;
  shortDescription: string;
  metaTitle: string;
  metaDescription: string;
  tags: string;
  gtin: string;
  mpn: string;
  countryOfOrigin: string;
  minOrderQuantity: number;
  maxOrderQuantity: number;
};

const emptyForm: FormState = {
  name: '', sku: '', barcode: '', hsnCode: '', description: '',
  brand: '', unit: 'pcs',
  costPrice: 0, sellingPrice: 0, mrpPrice: 0, taxRate: 0, weight: 0,
  dimensions: '',
  images: [], customQrImageUrl: '', isActive: true, hasVariants: false,
  categoryId: '', storeId: '',
  variants: [],
  productAttributes: [],
  sections: [],
  slug: '',
  shortDescription: '',
  metaTitle: '',
  metaDescription: '',
  tags: '',
  gtin: '',
  mpn: '',
  countryOfOrigin: 'India',
  minOrderQuantity: 1,
  maxOrderQuantity: 0,
};

const STEPS = ['basic', 'media', 'pricing', 'attributes', 'sections', 'variants', 'seo', 'review'] as const;
type StepId = (typeof STEPS)[number];
const STEP_LABELS: Record<StepId, string> = {
  basic: 'Basic info',
  media: 'Media & details',
  pricing: 'Pricing & tax',
  attributes: 'Attributes',
  sections: 'Sections',
  variants: 'Variants',
  seo: 'SEO & meta',
  review: 'Review & save',
};

const UNIT_OPTIONS: SearchableSelectOption[] = [
  { value: 'pcs', label: 'pcs', description: 'pieces' },
  { value: 'kg', label: 'kg', description: 'kilograms' },
  { value: 'g', label: 'g', description: 'grams' },
  { value: 'l', label: 'l', description: 'litres' },
  { value: 'ml', label: 'ml', description: 'millilitres' },
  { value: 'm', label: 'm', description: 'metres' },
  { value: 'cm', label: 'cm', description: 'centimetres' },
  { value: 'box', label: 'box' },
  { value: 'pack', label: 'pack' },
  { value: 'dozen', label: 'dozen' },
  { value: 'bag', label: 'bag' },
  { value: 'bottle', label: 'bottle' },
  { value: 'carton', label: 'carton' },
];

const loadCategoryOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await categoryService.all();
  return (rows ?? []).map((c) => ({
    value: c.categoryId ?? '', label: c.name, description: c.description, disabled: !c.categoryId,
  }));
};
const loadStoreOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await storeService.all();
  return (rows ?? []).map((s) => ({
    value: s.storeId ?? '', label: s.name, description: s.code, disabled: !s.storeId,
  }));
};
/**
 * Brand options — sourced from the live `/api/Brand/all` endpoint.
 * The legacy implementation scanned existing products for distinct
 * `brand` values, which broke once the lean `/Public/products`
 * list projection started returning `brand: null`.
 *
 * The form uses `allowCustom` so the admin can still type a
 * brand-new brand name without first creating a Brand row — useful
 * during initial seeding.
 */
const loadBrandOptions = async (): Promise<SearchableSelectOption[]> => {
  try {
    const rows = await brandService.all();
    return (rows ?? [])
      .filter((b) => b.isActive !== false)
      .map((b) => ({
        value: b.name,
        label: b.name,
        description: b.description,
        disabled: !b.name,
      }));
  } catch {
    return [];
  }
};

/**
 * Unit options — pulls from the live `/api/Unit/all` endpoint.
 * When the tenant has no Units seeded yet, we fall back to the
 * static SI / common-unit list so the dropdown is never empty.
 */
const loadUnitOptions = async (): Promise<SearchableSelectOption[]> => {
  try {
    const rows = await unitService.all();
    const live = (rows ?? [])
      .filter((u) => u.isActive !== false && (u.code || u.name))
      .map((u) => ({
        value: (u.code ?? u.name ?? '').toLowerCase(),
        label: (u.code ?? u.name ?? '').toLowerCase(),
        description: u.name,
      }));
    if (live.length > 0) return live;
  } catch {
    /* fall through */
  }
  return UNIT_OPTIONS;
};
const loadAttributeOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await attributeService.all();
  return (rows ?? []).map((a) => ({
    value: a.attributeId ?? '', label: a.name, description: a.description, disabled: !a.attributeId,
  }));
};
const loadAttributeValueOptions = async (attributeId?: string): Promise<SearchableSelectOption[]> => {
  if (!attributeId) return [];
  try {
    const rows = await attributeValueService.filter({ attributeId });
    return (rows ?? []).map((v) => ({
      value: v.attributeValueId ?? '', label: v.value, disabled: !v.attributeValueId,
    }));
  } catch {
    return [];
  }
};

const inputCls = 'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const selectCls = 'h-10 w-full appearance-none rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

function parseAttributesJson(raw?: string): VariantAttrPair[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed
        .filter((p) => p && typeof p === 'object')
        .map((p) => ({ attributeId: String(p.attributeId ?? ''), attributeValueId: String(p.attributeValueId ?? '') }));
    }
  } catch {
    /* ignore */
  }
  return [];
}
function serializeAttributePairs(pairs: VariantAttrPair[]): string {
  const cleaned = pairs.filter((p) => p.attributeId && p.attributeValueId);
  return cleaned.length ? JSON.stringify(cleaned) : '';
}

/**
 * Parse the optional `Product.sectionsJson` column into the form's
 * `sections` array. Defensive against malformed data — anything that
 * doesn't match the discriminator shape is dropped silently.
 */
function parseSectionsJson(raw: unknown): PanelSection[] {
  if (!raw || typeof raw !== 'string') return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const out: PanelSection[] = [];
    for (const s of parsed) {
      if (!s || typeof s !== 'object') continue;
      const name = String((s as { name?: unknown }).name ?? '');
      const type = String((s as { type?: unknown }).type ?? '');
      if (type === 'text') {
        out.push({
          name,
          type: 'text',
          content: String((s as { content?: unknown }).content ?? ''),
          features: Array.isArray((s as { features?: unknown }).features)
            ? (s as { features: unknown[] }).features.map(String)
            : [],
        });
      } else if (type === 'keyvalue') {
        const attrs = Array.isArray((s as { attributes?: unknown }).attributes)
          ? (s as { attributes: unknown[] }).attributes
              .filter((a): a is object => !!a && typeof a === 'object')
              .map((a) => ({
                name: String((a as { name?: unknown }).name ?? ''),
                value: String((a as { value?: unknown }).value ?? ''),
              }))
          : [];
        out.push({ name, type: 'keyvalue', attributes: attrs });
      }
    }
    return out;
  } catch {
    return [];
  }
}

export default function NewProduct() {
  const { id: rawId } = useParams<{ id?: string }>();
  // Edit URLs carry an obfuscated id (see apps/panel/src/utils/idCodec).
  // Decode here so productService.get is called with the real UUID.
  const id = decodeId(rawId);
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const isEdit = !!id && id !== 'new';
  const perms = useModulePerms('Product');
  const canSave = isEdit ? perms.canUpdate : perms.canCreate;

  /**
   * Pre-minted productId for CREATE mode — the panel used to hide the
   * ProductQrCard until after the first save (no id ⇒ no URL to encode).
   * Client wanted "upload or generate during creation" (SSO(2) ISSUE 3).
   * Minting a UUID up-front lets the QR card render immediately AND
   * ensures the encoded storefront URL resolves as soon as the product
   * lands in the DB (we send this id as the ProductId on the POST body,
   * and the backend accepts it as-is when non-empty).
   *
   * `crypto.randomUUID()` is safe (RFC 4122 v4, 122 bits of entropy) and
   * available in all evergreen browsers targeted by the panel bundle.
   * `useState(initializer)` runs once — the id is stable across renders
   * so save + subsequent QR redraws use the same value.
   */
  const [preMintedProductId] = useState(() =>
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : // Fallback for oddball runtimes without crypto.randomUUID —
        // NOT cryptographically strong, but the value only needs to be
        // globally unique among concurrent product creates for this tenant.
        `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}-${Math.random().toString(16).slice(2, 10)}`,
  );

  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [step, setStep] = useState<StepId>('basic');
  const [redTabs, setRedTabs] = useState<Set<StepId>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (isEdit && id) {
        setLoading(true); setLoadError(null);
        try {
          const rec = await productService.get(id);
          if (cancelled) return;
          const imgs = Array.isArray(rec.images) && rec.images.length > 0
            ? rec.images
            : (rec.imageUrl ? [rec.imageUrl] : []);
          const variants = Array.isArray(rec.variants) ? rec.variants : [];
          setForm({
            name: rec.name ?? '', sku: rec.sku ?? '', barcode: rec.barcode ?? '',
            hsnCode: rec.hsnCode ?? '', description: rec.description ?? '',
            brand: rec.brand ?? '', unit: rec.unit ?? 'pcs',
            costPrice: Number(rec.costPrice ?? 0),
            sellingPrice: Number(rec.sellingPrice ?? 0),
            mrpPrice: Number(rec.mrpPrice ?? 0),
            taxRate: Number(rec.taxRate ?? 0),
            weight: Number(rec.weight ?? 0),
            dimensions: rec.dimensions ?? '',
            images: imgs,
            customQrImageUrl: rec.customQrImageUrl ?? '',
            isActive: !!rec.isActive,
            hasVariants: !!rec.hasVariants,
            categoryId: rec.categoryId ?? '', storeId: rec.storeId ?? '',
            variants: variants.map((v) => ({ ...v, __attrPairs: parseAttributesJson(v.attributesJson) })),
            productAttributes: Array.isArray(rec.productAttributes) ? rec.productAttributes : [],
            sections: parseSectionsJson((rec as { sectionsJson?: unknown }).sectionsJson),
            slug: rec.slug ?? '',
            shortDescription: rec.shortDescription ?? '',
            metaTitle: rec.metaTitle ?? '',
            metaDescription: rec.metaDescription ?? '',
            tags: rec.tags ?? '',
            gtin: rec.gtin ?? '',
            mpn: rec.mpn ?? '',
            countryOfOrigin: rec.countryOfOrigin ?? 'India',
            minOrderQuantity: Number(rec.minOrderQuantity ?? 1),
            maxOrderQuantity: Number(rec.maxOrderQuantity ?? 0),
          });
        } catch (e) {
          const msg = e instanceof Error ? e.message : 'Product not found';
          setLoadError(msg); toastRef.current.error('Load failed', msg);
        } finally { if (!cancelled) setLoading(false); }
      } else setForm(emptyForm);
      setErrors({}); setStep('basic'); setRedTabs(new Set());
    }
    run();
    return () => { cancelled = true; };
  }, [id, isEdit]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));

  const validateStep = (s: StepId) => {
    const errs: typeof errors = {};
    if (s === 'basic' || s === 'review') {
      if (!form.name.trim()) errs.name = 'Name is required';
      if (!form.sku.trim()) errs.sku = 'SKU is required';
    }
    if (s === 'pricing' || s === 'review') {
      if (!(Number(form.sellingPrice) >= 0)) errs.sellingPrice = 'Must be ≥ 0';
      if (!(Number(form.costPrice) >= 0)) errs.costPrice = 'Must be ≥ 0';
      if (!(Number(form.taxRate) >= 0 && Number(form.taxRate) <= 100)) errs.taxRate = '0–100';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  const goNext = () => {
    const i = STEPS.indexOf(step); if (i >= STEPS.length - 1) return;
    if (!validateStep(step)) { toast.error('Fix errors', 'Some fields are invalid'); return; }
    const next = STEPS[i + 1]; setRedTabs((p) => { const n = new Set(p); n.delete(next); return n; }); setStep(next);
  };
  const goPrev = () => { const i = STEPS.indexOf(step); if (i === 0) return; setRedTabs((p) => new Set(p).add(step)); setStep(STEPS[i - 1]); };
  const basicValid = !!form.name.trim() && !!form.sku.trim();

  /* ---- variants ---- */
  const addVariant = () => {
    const nextVariants = [
      ...form.variants,
      { name: '', sku: '', barcode: '', costPrice: 0, sellingPrice: 0, mrpPrice: 0, attributesJson: '', isActive: true, __attrPairs: [] },
    ];
    setForm((f) => ({ ...f, variants: nextVariants, hasVariants: true }));
  };
  const updateVariant = (i: number, patch: Partial<FormState['variants'][number]>) => {
    const next = form.variants.slice(); next[i] = { ...next[i], ...patch }; set('variants', next);
  };
  const removeVariant = (i: number) => {
    const next = form.variants.filter((_, idx) => idx !== i);
    setForm((f) => ({ ...f, variants: next, hasVariants: next.length > 0 ? f.hasVariants : false }));
  };
  const addVariantAttr = (vi: number) => {
    const v = form.variants[vi]; if (!v) return;
    const pairs = [...(v.__attrPairs ?? []), { attributeId: '', attributeValueId: '' }];
    updateVariant(vi, { __attrPairs: pairs, attributesJson: serializeAttributePairs(pairs) });
  };
  const updateVariantAttr = (vi: number, pi: number, patch: Partial<VariantAttrPair>) => {
    const v = form.variants[vi]; if (!v) return;
    const pairs = (v.__attrPairs ?? []).slice();
    pairs[pi] = { ...pairs[pi], ...patch };
    if ('attributeId' in patch) pairs[pi].attributeValueId = '';
    updateVariant(vi, { __attrPairs: pairs, attributesJson: serializeAttributePairs(pairs) });
  };
  const removeVariantAttr = (vi: number, pi: number) => {
    const v = form.variants[vi]; if (!v) return;
    const pairs = (v.__attrPairs ?? []).filter((_, idx) => idx !== pi);
    updateVariant(vi, { __attrPairs: pairs, attributesJson: serializeAttributePairs(pairs) });
  };

  /* ---- product attributes ---- */
  const addPA = () => set('productAttributes', [...form.productAttributes, { attributeId: '', attributeValueId: '' }]);
  const updatePA = (i: number, patch: Partial<ProductAttribute>) => {
    const next = form.productAttributes.slice(); next[i] = { ...next[i], ...patch };
    if ('attributeId' in patch && patch.attributeId !== form.productAttributes[i]?.attributeId) {
      next[i].attributeValueId = '';
    }
    set('productAttributes', next);
  };
  const removePA = (i: number) => set('productAttributes', form.productAttributes.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    // Tab-wise validation. Save & Next on intermediate tabs just
    // advances; the API save runs only on the final Review tab.
    if (!validateStep(step)) { toast.error('Fix errors', 'Some fields are invalid'); return; }
    const stepIdx = STEPS.indexOf(step);
    const isLastStep = stepIdx === STEPS.length - 1;
    if (!isLastStep) {
      const next = STEPS[stepIdx + 1]!;
      setRedTabs((p) => { const n = new Set(p); n.delete(next); return n; });
      setStep(next);
      return;
    }
    if (!validateStep('review')) {
      toast.error('Fix errors', 'Some fields are invalid — check the red tabs');
      return;
    }
    setSaving(true);
    try {
      const cleanedPA = form.productAttributes.filter((p) => p.attributeId && p.attributeValueId);
      const cleanedVariants: ProductVariant[] = form.hasVariants
        ? form.variants.map(({ __attrPairs: _drop, ...rest }) => rest)
        : [];
      // Persist any uploaded base64 data URLs to the backend's blob
      // store first, then send the resulting hosted URLs with the
      // product save. Hosted URLs pass straight through. See
      // utils/persistImage.ts for the policy.
      let persistedImages: string[] = [];
      let persistedCustomQr: string | undefined;
      try {
        const raw = await Promise.all(
          form.images.map((img) => persistImage(img, 'product')),
        );
        persistedImages = raw.filter((u): u is string => !!u);
        // Custom QR — same policy as the gallery: hosted URLs pass
        // through, data: URLs upload to /api/Image/upload → hosted URL.
        // Empty stays empty (clears the column on subsequent PUT).
        persistedCustomQr = await persistImage(form.customQrImageUrl, 'product');
      } catch (e) {
        toast.error(
          'Image upload failed',
          e instanceof Error ? e.message : 'Could not upload the image.',
        );
        setSaving(false);
        return;
      }
      const body: Product = {
        // SSO(2) ISSUE 3 — send the pre-minted UUID on create so the QR
        // rendered on the Media tab resolves to a real storefront URL
        // right after save. Edit mode keeps its existing id.
        productId: isEdit ? id : preMintedProductId,
        name: form.name, sku: form.sku,
        barcode: form.barcode || undefined,
        hsnCode: form.hsnCode || undefined,
        description: form.description || undefined,
        brand: form.brand || undefined,
        unit: form.unit || undefined,
        costPrice: Number(form.costPrice) || undefined,
        sellingPrice: Number(form.sellingPrice),
        mrpPrice: Number(form.mrpPrice) || undefined,
        taxRate: Number(form.taxRate) || undefined,
        weight: Number(form.weight) || undefined,
        dimensions: form.dimensions || undefined,
        imageUrl: persistedImages[0] ?? undefined,
        images: persistedImages.length > 0 ? persistedImages : undefined,
        // Custom QR image URL — hosted URL after persistImage. Empty
        // string stays empty so the backend clears the column on PUT
        // (undefined would leave a previously-set value untouched).
        customQrImageUrl: persistedCustomQr ?? (isEdit ? '' : undefined),
        isActive: form.isActive,
        hasVariants: form.hasVariants,
        categoryId: form.categoryId || undefined,
        storeId: form.storeId || undefined,
        variants: cleanedVariants.length > 0 ? cleanedVariants : undefined,
        productAttributes: cleanedPA.length > 0 ? cleanedPA : undefined,
        // Serialise the dynamic accordion the storefront renders.
        // Backend column is `sectionsJson nvarchar(max)` (pending —
        // see BACKEND_AGENT_PROMPTS Item G); until it lands the
        // value is harmlessly dropped server-side.
        sectionsJson: form.sections.length > 0 ? JSON.stringify(form.sections) : undefined,
        // Industry-standard identifier + SEO fields. Backend columns
        // pending — see BACKEND_AGENT_PROMPTS Item J. Until they ship
        // these are silently dropped server-side, no harm done.
        slug: form.slug.trim() || undefined,
        shortDescription: form.shortDescription.trim() || undefined,
        metaTitle: form.metaTitle.trim() || undefined,
        metaDescription: form.metaDescription.trim() || undefined,
        tags: form.tags.trim() || undefined,
        gtin: form.gtin.trim() || undefined,
        mpn: form.mpn.trim() || undefined,
        countryOfOrigin: form.countryOfOrigin.trim() || undefined,
        minOrderQuantity: form.minOrderQuantity > 1 ? form.minOrderQuantity : undefined,
        maxOrderQuantity: form.maxOrderQuantity > 0 ? form.maxOrderQuantity : undefined,
      };
      if (isEdit) {
        await productService.update([body]);
        toast.success('Saved', `${form.name} updated`);
      } else {
        // SSO(2) ISSUE 3 — INCLUDE the pre-minted productId in the
        // create body so the row lands with the same id our QR card
        // encoded. Backend accepts a client-supplied ProductId via the
        // safety-net stamp added to ProductController.PostAsync
        // (mirrors the UserController.UserId pattern). Without this
        // step the storefront URL printed on the QR wouldn't resolve.
        // Live `POST /api/Product` returns `data: true` (boolean) instead
        // of the created entity, so we can't read the id off the response.
        // Recover it by filtering on the unique `sku` after create. Same
        // backend bug as SalesOrder + Store — see BACKEND_AGENT_PROMPTS.md
        // Item 4. Critically: do NOT `return` after navigate() — we still
        // need the tab-advance code below to run (Save & Next is broken
        // otherwise).
        await productService.create([body as unknown as ProductCreate]);
        toast.success('Saved', `${form.name} added`);
      }
      // Review-tab save succeeded (create or update) — return to the
      // grid list so the user sees their new/edited row in context,
      // matching the behaviour customers expect from edit mode.
      navigate('/panel/products');
    } catch (e) { toast.error('Save failed', e instanceof Error ? e.message : 'Failed'); }
    finally { setSaving(false); }
  };

  const tabStatus = useMemo<Record<string, TabStatus>>(() => {
    const m: Record<string, TabStatus> = {};
    redTabs.forEach((s) => (m[s] = 'danger'));
    return m;
  }, [redTabs]);

  const field = (label: string, key: keyof FormState, input: React.ReactNode, required?: boolean) => (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-content-secondary">{label} {required && <span className="text-danger">*</span>}</span>
      {input}
      {errors[key] && <span className="text-xs text-danger">{errors[key]}</span>}
    </label>
  );

  const basicStep = (
    <div className="grid gap-3 md:grid-cols-2">
      {field('Name', 'name', <input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Basmati Rice 5kg" className={inputCls} />, true)}
      {field('SKU', 'sku', <input value={form.sku} onChange={(e) => set('sku', e.target.value)} placeholder="RICE-BSM-5KG" className={inputCls} />, true)}
      {field('Barcode', 'barcode', <input value={form.barcode} onChange={(e) => set('barcode', e.target.value)} placeholder="EAN / UPC" className={inputCls} />)}
      {field('HSN code', 'hsnCode', <input value={form.hsnCode} onChange={(e) => set('hsnCode', e.target.value)} placeholder="HSN / SAC" className={inputCls} />)}

      <SearchableSelect
        label="Brand"
        value={form.brand}
        onChange={(v) => set('brand', v)}
        loadOptions={loadBrandOptions}
        placeholder="Pick or type a brand"
        searchPlaceholder="Search brands or type new..."
        emptyLabel="No brands yet — type to add one"
        allowCustom
      />
      <SearchableSelect
        label="Unit"
        value={form.unit}
        onChange={(v) => set('unit', v)}
        loadOptions={loadUnitOptions}
        placeholder="Pick or type a unit"
        searchPlaceholder="Search units or type new..."
        allowCustom
      />

      <SearchableSelect label="Category" value={form.categoryId} onChange={(v) => set('categoryId', v)} loadOptions={loadCategoryOptions} placeholder="Select a category" searchPlaceholder="Search categories..." />
      <SearchableSelect label="Store" value={form.storeId} onChange={(v) => set('storeId', v)} loadOptions={loadStoreOptions} placeholder="Select a store" searchPlaceholder="Search stores..." />

      {field('Status', 'isActive',
        <select value={form.isActive ? 'active' : 'inactive'} onChange={(e) => set('isActive', e.target.value === 'active')} className={selectCls}>
          <option value="active">active</option>
          <option value="inactive">inactive</option>
        </select>)}
      {field('Has variants', 'hasVariants',
        <select value={form.hasVariants ? 'yes' : 'no'} onChange={(e) => set('hasVariants', e.target.value === 'yes')} className={selectCls}>
          <option value="no">No</option>
          <option value="yes">Yes</option>
        </select>)}
    </div>
  );

  const mediaStep = (
    <div className="grid gap-3">
      <ImageGallery
        label="Product images"
        value={form.images}
        onChange={(next) => set('images', next)}
      />
      {/* Dynamic per-product QR — rendered in BOTH create and edit modes
          now (SSO(2) ISSUE 3). In create mode the id is a client-side
          pre-minted UUID that we also send as ProductId on the POST body,
          so the QR resolves to a live storefront URL the moment the
          product hits the DB. If the admin uploads a custom QR image
          below, the card renders that instead of the generated code. */}
      <ProductQrCard
        productId={isEdit ? id : preMintedProductId}
        productName={form.name}
        sku={form.sku}
        customQrImageUrl={form.customQrImageUrl}
      />
      {/* Custom QR upload — admin can supply an externally-generated QR
          (branded, stylised, etc.) that will replace the auto-generated
          canvas above. Empty ⇒ the generated QR wins. Persisted via
          persistImage on save (any data: URL uploads to /api/Image/upload
          and lands as a hosted /uploads/... path). */}
      <div className="rounded-md border border-border p-3">
        <div className="mb-2 flex flex-col gap-1">
          <span className="text-xs font-medium text-content-secondary">
            Or upload a custom QR image
          </span>
          <span className="text-[11px] text-content-muted">
            Optional. Provide a pre-generated QR (branded / stylised) and it
            replaces the auto-generated code above. Leave empty to keep the
            generated QR that encodes this product's storefront URL.
          </span>
        </div>
        <ImagePicker
          value={form.customQrImageUrl}
          onChange={(next) => set('customQrImageUrl', next)}
          hint="Recommend a square PNG (256×256 or larger)."
          maxBytes={512 * 1024}
        />
      </div>
      {field('Description', 'description',
        <textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={5} placeholder="Detailed description..." className={`${inputCls} h-auto py-2`} />)}
      {field('Weight (kg)', 'weight',
        <NumberField min={0} step="0.001" value={form.weight} onValueChange={(n) => set('weight', n)} className={inputCls} />)}
      {field('Dimensions', 'dimensions',
        <input
          value={form.dimensions}
          onChange={(e) => set('dimensions', e.target.value)}
          placeholder="e.g. 10 × 8 × 3 inches"
          maxLength={64}
          className={inputCls}
        />)}
    </div>
  );

  const pricingStep = (
    <div className="grid gap-3 md:grid-cols-4">
      {field('Cost price (₹)', 'costPrice', <NumberField min={0} step="0.01" value={form.costPrice} onValueChange={(n) => set('costPrice', n)} className={inputCls} />)}
      {field('Selling price (₹)', 'sellingPrice', <NumberField min={0} step="0.01" value={form.sellingPrice} onValueChange={(n) => set('sellingPrice', n)} className={inputCls} />, true)}
      {field('MRP (₹)', 'mrpPrice', <NumberField min={0} step="0.01" value={form.mrpPrice} onValueChange={(n) => set('mrpPrice', n)} className={inputCls} />)}
      {field('Tax rate (%)', 'taxRate', <NumberField min={0} max={100} step="0.01" value={form.taxRate} onValueChange={(n) => set('taxRate', n)} className={inputCls} />)}
    </div>
  );

  const attributesStep = (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-content-muted">Link this product to global attributes (e.g. Colour · Red).</span>
        <Button type="button" size="sm" variant="outline" onClick={addPA}>
          <Plus className="h-3.5 w-3.5" /> Add attribute
        </Button>
      </div>
      {form.productAttributes.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-4 text-center text-xs text-content-muted">
          No attributes linked yet.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {form.productAttributes.map((pa, i) => (
            <div key={`pa-${i}`} className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
              <SearchableSelect
                label={i === 0 ? 'Attribute' : undefined}
                value={pa.attributeId ?? ''}
                onChange={(v) => updatePA(i, { attributeId: v })}
                loadOptions={loadAttributeOptions}
                placeholder="Select attribute"
                searchPlaceholder="Search attributes..."
              />
              <SearchableSelect
                key={`pa-val-${i}-${pa.attributeId ?? 'none'}`}
                label={i === 0 ? 'Value' : undefined}
                value={pa.attributeValueId ?? ''}
                onChange={(v) => updatePA(i, { attributeValueId: v })}
                loadOptions={() => loadAttributeValueOptions(pa.attributeId)}
                placeholder={pa.attributeId ? 'Select value' : 'Pick attribute first'}
                searchPlaceholder="Search values..."
                disabled={!pa.attributeId}
              />
              <button
                type="button"
                aria-label="Remove attribute"
                onClick={() => removePA(i)}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border text-danger hover:bg-surface-card-hover"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Dynamic accordion authoring — one section block per <details> on
  // the storefront. Mirrors the JSON contract used by the AI
  // product-creation assistant; see SectionsEditor.tsx for the shape.
  const sectionsStep = (
    <div className="flex flex-col gap-3">
      <SectionsEditor
        value={form.sections}
        onChange={(next) => set('sections', next)}
      />
    </div>
  );

  // SEO + industry-standard identifier fields. Most ecommerce
  // platforms (Shopify, WooCommerce, BigCommerce) expose a similar
  // set: slug, short description, meta title / description, tags,
  // GTIN/MPN/origin, min order qty. Each input is sized to its
  // canonical max-length so admins can't write past the limit.
  const seoStep = (
    <div className="grid gap-3 md:grid-cols-2">
      {field(
        'URL slug',
        'slug',
        <input
          value={form.slug}
          onChange={(e) => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, ''))}
          placeholder="auto-derived from name when blank"
          maxLength={120}
          className={`${inputCls} font-mono`}
        />,
      )}
      {field(
        'Short description',
        'shortDescription',
        <input
          value={form.shortDescription}
          onChange={(e) => set('shortDescription', e.target.value)}
          placeholder="One-line teaser for cards / search snippets"
          maxLength={200}
          className={inputCls}
        />,
      )}
      {field(
        'Meta title',
        'metaTitle',
        <input
          value={form.metaTitle}
          onChange={(e) => set('metaTitle', e.target.value)}
          placeholder="SEO <title> override (≤ 70 chars)"
          maxLength={70}
          className={inputCls}
        />,
      )}
      {field(
        'Meta description',
        'metaDescription',
        <input
          value={form.metaDescription}
          onChange={(e) => set('metaDescription', e.target.value)}
          placeholder="SEO meta description (≤ 160 chars)"
          maxLength={160}
          className={inputCls}
        />,
      )}
      {field(
        'Tags',
        'tags',
        <input
          value={form.tags}
          onChange={(e) => set('tags', e.target.value)}
          placeholder="comma-separated · e.g. ayurveda, wellness, herbal"
          maxLength={500}
          className={inputCls}
        />,
      )}
      {field(
        'Country of origin',
        'countryOfOrigin',
        <input
          value={form.countryOfOrigin}
          onChange={(e) => set('countryOfOrigin', e.target.value)}
          placeholder="e.g. India"
          maxLength={64}
          className={inputCls}
        />,
      )}
      {field(
        'GTIN (EAN/UPC)',
        'gtin',
        <input
          value={form.gtin}
          onChange={(e) => set('gtin', e.target.value)}
          placeholder="14-digit barcode standard"
          maxLength={14}
          className={`${inputCls} font-mono`}
        />,
      )}
      {field(
        'MPN',
        'mpn',
        <input
          value={form.mpn}
          onChange={(e) => set('mpn', e.target.value)}
          placeholder="Manufacturer Part Number"
          maxLength={64}
          className={`${inputCls} font-mono`}
        />,
      )}
      {field(
        'Min order qty',
        'minOrderQuantity',
        <NumberField
          min={1}
          value={form.minOrderQuantity}
          onValueChange={(n) => set('minOrderQuantity', n || 1)}
          defaultEmpty={1}
          className={inputCls}
        />,
      )}
      {field(
        'Max order qty',
        'maxOrderQuantity',
        <NumberField
          min={0}
          value={form.maxOrderQuantity}
          onValueChange={(n) => set('maxOrderQuantity', n)}
          placeholder="0 = no limit"
          className={inputCls}
        />,
      )}
    </div>
  );

  const variantsStep = (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-content-muted">
          Add each variant with its own SKU, price, and attribute combination.
        </span>
        <Button type="button" size="sm" variant="outline" onClick={addVariant}>
          <Plus className="h-3.5 w-3.5" /> Add variant
        </Button>
      </div>
      {form.variants.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-4 text-center text-xs text-content-muted">
          No variants yet. Click "Add variant" to start — "Has variants" will turn on automatically.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {form.variants.map((v, i) => (
            <div key={`variant-${i}`} className="rounded-md border border-border bg-surface-card-hover p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-content-muted">Variant {i + 1}</span>
                <button type="button" aria-label="Remove variant" onClick={() => removeVariant(i)}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-danger hover:bg-surface-card">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="grid gap-2 md:grid-cols-3">
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-medium text-content-secondary">Name</span>
                  <input value={v.name} onChange={(e) => updateVariant(i, { name: e.target.value })} placeholder="Red · Large" className={inputCls} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-medium text-content-secondary">SKU</span>
                  <input value={v.sku} onChange={(e) => updateVariant(i, { sku: e.target.value })} placeholder="RICE-BSM-5KG-R-L" className={inputCls} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-medium text-content-secondary">Barcode</span>
                  <input value={v.barcode ?? ''} onChange={(e) => updateVariant(i, { barcode: e.target.value })} placeholder="EAN" className={inputCls} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-medium text-content-secondary">Cost (₹)</span>
                  <NumberField min={0} step="0.01" value={v.costPrice ?? 0} onValueChange={(n) => updateVariant(i, { costPrice: n })} className={inputCls} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-medium text-content-secondary">Selling (₹)</span>
                  <NumberField min={0} step="0.01" value={v.sellingPrice ?? 0} onValueChange={(n) => updateVariant(i, { sellingPrice: n })} className={inputCls} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-medium text-content-secondary">MRP (₹)</span>
                  <NumberField min={0} step="0.01" value={v.mrpPrice ?? 0} onValueChange={(n) => updateVariant(i, { mrpPrice: n })} className={inputCls} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-medium text-content-secondary">Status</span>
                  <select value={v.isActive ? 'active' : 'inactive'} onChange={(e) => updateVariant(i, { isActive: e.target.value === 'active' })} className={selectCls}>
                    <option value="active">active</option>
                    <option value="inactive">inactive</option>
                  </select>
                </label>
              </div>
              {/* Nested variant attributes */}
              <div className="mt-3 border-t border-border pt-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-[11px] font-medium text-content-secondary">Variant attributes</span>
                  <button type="button" onClick={() => addVariantAttr(i)}
                    className="flex items-center gap-1 rounded-md border border-border px-2 py-0.5 text-[11px] text-content-secondary hover:bg-surface-card">
                    <Plus className="h-3 w-3" /> Add
                  </button>
                </div>
                {(v.__attrPairs ?? []).length === 0 ? (
                  <div className="rounded border border-dashed border-border px-3 py-2 text-[11px] text-content-muted">
                    No attributes for this variant.
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {(v.__attrPairs ?? []).map((p, pi) => (
                      <div key={`va-${i}-${pi}`} className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
                        <SearchableSelect
                          value={p.attributeId}
                          onChange={(val) => updateVariantAttr(i, pi, { attributeId: val })}
                          loadOptions={loadAttributeOptions}
                          placeholder="Attribute"
                          searchPlaceholder="Search attributes..."
                        />
                        <SearchableSelect
                          key={`va-val-${i}-${pi}-${p.attributeId || 'none'}`}
                          value={p.attributeValueId}
                          onChange={(val) => updateVariantAttr(i, pi, { attributeValueId: val })}
                          loadOptions={() => loadAttributeValueOptions(p.attributeId)}
                          placeholder={p.attributeId ? 'Value' : 'Pick attribute first'}
                          searchPlaceholder="Search values..."
                          disabled={!p.attributeId}
                        />
                        <button type="button" aria-label="Remove" onClick={() => removeVariantAttr(i, pi)}
                          className="flex h-10 w-10 items-center justify-center rounded-md border border-border text-danger hover:bg-surface-card">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {v.attributesJson && (
                  <div className="mt-2 font-mono text-[10px] text-content-muted">
                    attributesJson: <span className="break-all">{v.attributesJson}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const reviewStep = (
    <div className="grid gap-3">
      <div className="flex items-center gap-2 text-success"><CheckCircle2 className="h-4 w-4" /><span className="text-sm font-medium">Ready to save</span></div>
      <div className="rounded-md border border-border bg-surface-card-hover p-4 text-sm">
        {form.images.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {form.images.map((src, i) => (
              <div key={i} className="relative h-20 w-20 overflow-hidden rounded-md border border-border bg-surface-card">
                <img src={src} alt={`img-${i}`} className="h-full w-full object-cover" />
                {i === 0 && <span className="absolute left-0.5 top-0.5 rounded-sm bg-primary px-1 py-0.5 text-[9px] text-primary-foreground">Primary</span>}
              </div>
            ))}
          </div>
        )}
        <div className="grid gap-2 md:grid-cols-2">
          <Row label="Name" value={form.name} />
          <Row label="SKU" value={form.sku} />
          <Row label="Brand" value={form.brand} />
          <Row label="Unit" value={form.unit} />
          <Row label="Barcode" value={form.barcode} />
          <Row label="HSN" value={form.hsnCode} />
          <Row label="Category" value={<NameCell kind="category" id={form.categoryId} />} />
          <Row label="Store" value={<NameCell kind="store" id={form.storeId} />} />
          <Row label="Cost" value={`₹ ${Number(form.costPrice).toLocaleString('en-IN')}`} />
          <Row label="Selling" value={`₹ ${Number(form.sellingPrice).toLocaleString('en-IN')}`} />
          <Row label="MRP" value={`₹ ${Number(form.mrpPrice).toLocaleString('en-IN')}`} />
          <Row label="Tax" value={`${form.taxRate}%`} />
          <Row label="Weight" value={`${form.weight} kg`} />
          <Row label="Dimensions" value={form.dimensions} />
          <Row label="Status" value={<StatusBadge status={form.isActive ? 'active' : 'inactive'} />} />
        </div>
        {form.description && (
          <div className="mt-3 rounded border border-border bg-surface-card p-3 text-xs text-content-secondary">{form.description}</div>
        )}
        {form.productAttributes.length > 0 && (
          <div className="mt-3">
            <div className="mb-1 text-xs font-medium text-content-muted">Attributes ({form.productAttributes.filter((p) => p.attributeId && p.attributeValueId).length})</div>
            <div className="flex flex-wrap gap-2">
              {form.productAttributes.map((p, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-full bg-primary-light px-2 py-0.5 text-xs font-medium text-primary">
                  <NameCell kind="attribute" id={p.attributeId} />
                  <span className="opacity-60">·</span>
                  <NameCell kind="attributeValue" id={p.attributeValueId} />
                </span>
              ))}
            </div>
          </div>
        )}
        {form.hasVariants && form.variants.length > 0 && (
          <div className="mt-3">
            <div className="mb-1 text-xs font-medium text-content-muted">Variants ({form.variants.length})</div>
            <div className="flex flex-wrap gap-2">
              {form.variants.map((v, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-full bg-info/15 px-2 py-0.5 text-xs font-medium text-info">
                  {v.name || v.sku || `#${i + 1}`}
                </span>
              ))}
            </div>
          </div>
        )}
        {form.sections.length > 0 && (
          // Compact summary so reviewers see the section names + their
          // type before saving. Click-through editing happens on the
          // Sections tab.
          <div className="mt-3">
            <div className="mb-1 text-xs font-medium text-content-muted">
              Storefront sections ({form.sections.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {form.sections.map((s, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-xs font-medium text-primary"
                >
                  {s.name || `#${i + 1}`}
                  <span className="text-[10px] uppercase opacity-70">· {s.type}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const tabs = [
    { id: 'basic' as const, label: STEP_LABELS.basic, content: basicStep },
    { id: 'media' as const, label: STEP_LABELS.media, content: mediaStep },
    { id: 'pricing' as const, label: STEP_LABELS.pricing, content: pricingStep },
    { id: 'attributes' as const, label: STEP_LABELS.attributes, content: attributesStep },
    { id: 'sections' as const, label: STEP_LABELS.sections, content: sectionsStep },
    { id: 'variants' as const, label: STEP_LABELS.variants, content: variantsStep },
    { id: 'seo' as const, label: STEP_LABELS.seo, content: seoStep },
    { id: 'review' as const, label: STEP_LABELS.review, content: reviewStep },
  ];
  const isFirst = STEPS.indexOf(step) === 0;
  const isLast = STEPS.indexOf(step) === STEPS.length - 1;

  return (
    <div className="-m-6 p-[3px]">
      {loadError && <Alert variant="danger" className="mb-3">{loadError}</Alert>}
      <div className="mb-[3px]">
        <Tabs tabs={tabs} activeId={step}
          onChange={(sid) => {
            const t = STEPS.indexOf(sid as StepId); const c = STEPS.indexOf(step);
            if (t < c) setRedTabs((p) => { const n = new Set(p); for (let i = t + 1; i <= c; i++) n.add(STEPS[i]); return n; });
            else if (t > c) setRedTabs((p) => { const n = new Set(p); for (let i = c + 1; i <= t; i++) n.delete(STEPS[i]); return n; });
            setStep(sid as StepId);
          }}
          tabStatus={tabStatus} barOnly />
      </div>
      <Card>
        <div className="min-h-[180px]">{loading ? <div className="text-sm text-content-muted">Loading…</div> : tabs.find((t) => t.id === step)?.content}</div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={goPrev} disabled={isFirst}><ChevronLeft className="h-4 w-4" /> Previous</Button>
            {!isLast && <Button type="button" onClick={goNext}>Next <ChevronRight className="h-4 w-4" /></Button>}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={() => navigate('/panel/products')}>Cancel</Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={!basicValid || saving || (isLast && !canSave)}
              title={isLast && !canSave ? `You don't have permission to ${isEdit ? 'update' : 'create'} products` : undefined}
            >
              <Save className="h-4 w-4" /> {saving ? 'Saving…' : isLast ? (isEdit ? 'Update' : 'Save') : 'Next →'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded border border-border bg-surface-card px-3 py-2">
      <span className="text-xs text-content-muted">{label}</span>
      <span className="text-sm font-medium text-content-primary">{value !== '' && value != null ? value : <em className="text-content-muted">—</em>}</span>
    </div>
  );
}
